"use client";

import { ArrowRight } from "lucide-react";
import Link from "next/link";
import { motion } from "framer-motion";
import WhatsAppButton from "@/components/WhatsApp/WhatsAppButton";
import { whatsappMessages } from "@/utils/whatsapp";
import EnrollButton from "@/components/LeadForm/EnrollButton";
import { FORM_CONTEXTS } from "@/utils/innovlistWidget";

function CardEnquiryForm() {
    return (
        <div className="mt-4 flex flex-col gap-3">
            <EnrollButton
                label="Get Free Counseling"
                variant="primary"
                size="md"
                context={FORM_CONTEXTS.CARD_ENQUIRY}
                fullWidth={true}
                icon={false}
            />
            <p className="text-xs text-gray-500 text-center mt-2">
                Free session - No commitment required
            </p>
            <WhatsAppButton
                variant="solid"
                size="md"
                label="Chat Directly on WhatsApp"
                message={whatsappMessages.counseling}
                context="home_card_form"
                className="w-full justify-center mt-1"
            />
        </div>
    );
}

export function InfoCards() {
    return (
        <section className="py-12 bg-white">
            <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Card 1 — Learn Anytime, Anywhere */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0, duration: 0.5 }}
                        className="p-8 rounded-2xl border border-transparent transition-all shadow-sm bg-[#FFEFEF] hover:border-red-200"
                    >
                        <span className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block bg-red-100 text-red-600">
                            Self-Paced
                        </span>
                        <h3 className="text-2xl font-bold text-[#002147] mb-2">
                            Learn Anytime, Anywhere
                        </h3>
                        <p className="text-gray-600 text-[18px] mb-8 leading-relaxed">
                            Access live and recorded sessions from anywhere in the world. Our interactive online classroom lets you learn at your own schedule without compromising on quality or depth.
                        </p>
                        <Link
                            href="/courses"
                            className="flex items-center gap-2 font-bold text-[#002147] group transition-colors hover:text-blue-600"
                        >
                            Explore Courses <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>

                    {/* Card 2 — Unlock New Career Opportunities (inline enquiry form) */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1, duration: 0.5 }}
                        className="p-8 rounded-2xl border border-transparent transition-all shadow-sm bg-[#EFFCFF] hover:border-blue-200"
                    >
                        <span className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block bg-blue-100 text-blue-600">
                            Career Growth
                        </span>
                        <h3 className="text-2xl font-bold text-[#002147] mb-2">
                            Unlock New Career Opportunities
                        </h3>
                        <p className="text-gray-600 text-[18px] mb-4 leading-relaxed">
                            Our courses are built around real-world industry requirements. Every curriculum is designed to get you interview-ready and connect you with active job openings through our placement network.
                        </p>
                        <CardEnquiryForm />
                    </motion.div>

                    {/* Card 3 — Get Corporate Training for Your Team */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="p-8 rounded-2xl border border-transparent transition-all shadow-sm bg-[#F8EFFF] hover:border-purple-200"
                    >
                        <span className="px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6 inline-block bg-purple-100 text-purple-600">
                            Entrepreneurship
                        </span>
                        <h3 className="text-2xl font-bold text-[#002147] mb-2">
                            Get Corporate Training for Your Team
                        </h3>
                        <p className="text-gray-600 text-[18px] mb-8 leading-relaxed">
                            Empower your workforce with customized IT training programs. We design group learning experiences tailored to your company&apos;s technology stack and business goals.
                        </p>
                        <Link
                            href="/about-us"
                            className="flex items-center gap-2 font-bold text-[#002147] group transition-colors hover:text-blue-600"
                        >
                            Learn More <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
